// Pure helpers shared across GA4 panels.
// Sorting, weighted averages, export (CSV/PDF), and label humanization.
// No React.

import { EVENT_LABELS } from './constants';

// jspdf + jspdf-autotable are heavy (~kBs) and only needed on PDF export, so
// they're loaded on demand inside downloadPdf instead of in the initial bundle.

/* ----------------------------- text helpers ----------------------------- */
export const formatLandingPage = (raw) => {
  if (raw == null || raw === '') return '(unknown)';
  if (raw === '/') return '/ (homepage)';
  return raw;
};

/* ----------------------------- sorting ----------------------------- */
export const sortRows = (rows, key, dir) => {
  if (!key) return rows;
  return [...rows].sort((rowA, rowB) => {
    let valueA = rowA[key], valueB = rowB[key];
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;
    if (typeof valueA === 'string') return dir === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    return dir === 'asc' ? valueA - valueB : valueB - valueA;
  });
};

/* ----------------------------- weighted average ----------------------------- */
// Used for bounce_rate, engagement_rate, avg_session_duration, pages_per_session
// — GA4 rates that must be weighted by sessions, never plain-averaged.
export const weightedAvg = (rows, field, weightField) => {
  let sumProduct = 0, sumWeight = 0;
  for (const row of rows) {
    const value = row[field];
    const weight = row[weightField] || 0;
    if (value != null) { sumProduct += value * weight; sumWeight += weight; }
  }
  return sumWeight > 0 ? sumProduct / sumWeight : null;
};

/* ----------------------------- numeric formatters -----------------------------
 * GA4 spec (bounce-rate precision) wants percentages to ONE decimal (e.g. "27.0%"),
 * where the cross-feature fmtPercent renders two ("27.00%"). Rather than change the
 * shared helper (used by CI/EI/Google Ads), GA4 defines its own 1-decimal fmtPercent
 * here and re-exports the other formatters from cross-feature unchanged — so a GA4
 * panel imports every formatter from one place and only the percent precision differs.
 */
export { fmtNumber, fmtCurrency, fmtDuration, fmtDateLabel, fmtCount, fmtDate } from '../../shared/format';

// fmtPercent — INPUT IS A RATIO (same contract as shared/format.js fmtPercent,
// only the precision differs: 1 decimal here vs 2). Multiplies by 100:
// 0.27 → "27.0%". GA4 rates (bounce_rate, engagement_rate) arrive as 0–1
// fractions, so they belong here.
//
// ⚠️ CONTRACT TRAP: do NOT confuse with the ALREADY-PERCENT `fmtPct` in
// competitive-intelligence/ and event-intelligence/ (27 → "27.0%", no ×100).
// Same-looking name, different input. A GA4 ratio through CI's fmtPct renders
// "0.3%"; a CI percent through this renders "2700.0%".
export const fmtPercent = (v) => {
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
};

/* ----------------------------- export helpers -----------------------------
 * CSV / PDF download helpers live in the cross-feature src/components/shared/exportData.
 * Re-exported here so existing GA4 callers (`import { downloadCsv, downloadPdf } from "../shared"`) keep working.
 * `makeExport` builds a single-spec CSV+PDF column definition (no drift).
 */
export { downloadCsv, downloadPdf, makeExport } from '../../shared/exportData';

/* ----------------------------- event name humanization ----------------------------- */
export const humanizeEvent = (name) => {
  if (!name) return 'Unknown';
  if (EVENT_LABELS[name]) return EVENT_LABELS[name];
  const label = name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  // Singularize a leading count of 1 on time-unit milestones: "1 Minutes" -> "1 Minute"
  return label.replace(/^1 (Second|Minute|Hour|Day|Week|Month|Year)s\b/, '1 $1');
};
